import React from "react";
import { Trash2, Package, Truck, ShoppingBag, ArrowRight } from "lucide-react";
import { calculatePrice, formatINR } from "../utils/pricingEngine";

/**
 * PricingTool
 * Shows the live cart — all items added via ProductLinkPricing.
 * Each item must carry both:
 *   item.priceExclGST   — Amazon price excl. GST (our quote base)
 *   item.amazonPrice    — Amazon price incl. GST (classification threshold)
 */
const PricingTool = ({
  cartItems = [],
  onRemove,
  onUpdateQty,
  onUpdateDel,
  subtotal = 0,
  onProceed,
}) => {
  if (cartItems.length === 0) {
    return (
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-5">
            <ShoppingBag className="h-9 w-9 text-gray-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Your cart is empty</h3>
          <p className="text-gray-400">
            Paste a product link above and add it to unlock the best price for single or bulk orders.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing-tool" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Inquiry Cart
            <span className="ml-3 text-base font-normal text-gray-400">
              {cartItems.length} item{cartItems.length > 1 ? "s" : ""}
            </span>
          </h2>
        </div>

        {/* Items */}
        <div className="space-y-4 mb-8">
          {cartItems.map((item) => {
            // Use priceExclGST as base; fall back to price / 1.18 if not present
            const basePriceExclGST   = item.priceExclGST ?? Math.round(item.amazonPrice / 1.18);
            const amazonPriceWithGST = item.amazonPrice;

            const pricing = calculatePrice(
              basePriceExclGST,
              amazonPriceWithGST,
              item.weight,
              item.quantity,
              item.deliveryType
            );

            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
              >
                <div className="flex gap-4">
                  {/* Image */}
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-16 h-16 object-contain rounded-xl bg-gray-50 border border-gray-100 p-1 flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-gray-100 flex-shrink-0" />
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">
                          {item.name}
                        </p>
                        {item.brand && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.brand}</p>
                        )}
                      </div>
                      <button
                        onClick={() => onRemove?.(item.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Controls row */}
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {/* Qty */}
                      <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-200">
                        <Package className="h-3.5 w-3.5 text-gray-400" />
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            onUpdateQty?.(item.id, Math.max(1, parseInt(e.target.value) || 1))
                          }
                          className="w-14 text-sm font-medium text-gray-800 bg-transparent outline-none"
                        />
                      </div>

                      {/* Delivery */}
                      <div className="flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5 text-gray-400" />
                        <select
                          value={item.deliveryType}
                          onChange={(e) => onUpdateDel?.(item.id, e.target.value)}
                          className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none"
                        >
                          <option value="normal">Standard (7–10d)</option>
                          <option value="express">Express (48h)</option>
                        </select>
                      </div>

                      {/* Discount badge */}
                      {/* {pricing.discountPct > 0 && (
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-100">
                          −{pricing.discountPct}%
                        </span>
                      )} */}

                      {/* Weight/price class badges */}
                      {/* <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                        pricing.isLight
                          ? "bg-blue-50 text-blue-600 border-blue-100"
                          : "bg-orange-50 text-orange-600 border-orange-100"
                      }`}>
                        {pricing.isLight ? "Light" : "Heavy"}
                      </span> */}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs text-gray-400 mb-0.5">Our Price (Excl. GST)</p>
                    <p className="text-lg font-bold text-blue-600">
                      ₹{formatINR(pricing.finalPrice)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      ₹{formatINR(pricing.perUnit)}/unit
                    </p>
                    {/* Amazon strikethrough (with GST) */}
                    {/* <p className="text-xs text-gray-300 line-through mt-0.5">
                      ₹{formatINR(amazonPriceWithGST * item.quantity)} (Amazon)
                    </p> */}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wide">
            Summary
          </h3>
          <div className="border-t border-gray-100 pt-3 flex justify-between text-lg font-bold text-gray-900">
            <span>Total (Excl. GST)</span>
            <span className="text-blue-600">₹{formatINR(subtotal)}</span>
          </div>
          <p className="text-xs text-gray-400 mt-2">GST @ 18% applicable at billing</p>

          <button
            onClick={onProceed}
            className="w-full mt-6 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition text-base"
          >
            Proceed to Quotation
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PricingTool;